import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Users, PackagePlus } from 'lucide-react';
import { styles, Modal } from '../theme';

import { API_URL, fetchWithAuth } from '../api';
import { usePermissions } from '../context/PermissionContext';
import PosCategoryAssignItemsModal from './PosCategoryAssignItemsModal';

const emptyForm = { name: '', color: '#6366F1', is_active: 1 };

const PosCategories = ({ token, onCreateItem }) => {
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [assigningCategory, setAssigningCategory] = useState(null);

  useEffect(() => {
    fetchCategories(searchQuery);
  }, [searchQuery]);

  const fetchCategories = async (search = '') => {
    try {
      const queryParams = new URLSearchParams({ page: 1, per_page: 100, search });
      const res = await fetchWithAuth(`${API_URL}/pos/categories?${queryParams.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setCategories(data.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handleSubmit = async () => {
    if (!form.name) {
      alert('Missing required field (Name)');
      return;
    }

    try {
      const url = editingId ? `${API_URL}/pos/categories/${editingId}` : `${API_URL}/pos/categories`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        fetchCategories(searchQuery);
        setShowForm(false);
        setEditingId(null);
        setForm(emptyForm);
      } else {
        const errData = await res.json();
        alert('Error: ' + (errData.error || JSON.stringify(errData.error)));
      }
    } catch (err) {
      console.error('Error saving category:', err);
    }
  };

  const handleEdit = (category) => {
    setForm({
      name: category.name || '',
      color: category.color || '#6366F1',
      is_active: category.is_active === undefined ? 1 : category.is_active
    });
    setEditingId(category.id);
    setShowForm(true);
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Delete category "${category.name}"?`)) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/pos/categories/${category.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        fetchCategories(searchQuery);
      } else {
        const errData = await res.json();
        alert('Error: ' + (errData.error || JSON.stringify(errData.error)));
      }
    } catch (err) {
      console.error('Error deleting category:', err);
    }
  };

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>POS Categories</h1>
        {canCreate('pos_categories') && (
          <button onClick={() => { setShowForm(true); setEditingId(null); setForm(emptyForm); }} style={styles.button}>
            <Plus style={{ width: '20px', height: '20px' }} /> Add Category
          </button>
        )}
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={`${editingId ? 'Edit' : 'Add'} Category`}
        maxWidth="450px"
      >
        <div style={styles.formGroup}>
          <label style={styles.label}>Category Name *</label>
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={styles.input} />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Category Color</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="color"
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              style={{ width: '48px', height: '40px', padding: '2px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}
            />
            <input
              type="text"
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              style={{ ...styles.input, flex: 1 }}
            />
          </div>
        </div>
        {editingId && (
          <div style={styles.formGroup}>
            <label style={styles.label}>
              <input
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })}
                style={{ marginRight: '8px' }}
              />
              Active
            </label>
          </div>
        )}
        <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button onClick={() => setShowForm(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>Cancel</button>
          <button onClick={handleSubmit} style={styles.button}>Save</button>
        </div>
      </Modal>

      <div style={{ ...styles.card, marginBottom: '20px' }}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Search</label>
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.input}
          />
        </div>
      </div>

      {categories.length === 0 ? (
        <div style={{ ...styles.card, textAlign: 'center', color: '#6b7280', padding: '40px' }}>
          No categories yet.
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: '16px'
        }}>
          {categories.map(cat => (
            <div key={cat.id} style={{
              ...styles.card,
              borderTop: `6px solid ${cat.color}`,
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '18px', height: '18px', borderRadius: '4px',
                  backgroundColor: cat.color, flexShrink: 0
                }} />
                <div style={{ fontWeight: 600, fontSize: '16px', flex: 1 }}>{cat.name}</div>
                {!cat.is_active && (
                  <span style={{ fontSize: '11px', color: '#b91c1c' }}>Inactive</span>
                )}
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                {cat.item_count} item{cat.item_count === 1 ? '' : 's'} assigned
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: 'auto', flexWrap: 'wrap' }}>
                {canUpdate('pos_categories') && (
                  <button
                    onClick={() => setAssigningCategory(cat)}
                    style={{ ...styles.button, ...styles.buttonSecondary, padding: '8px 12px', fontSize: '13px' }}
                  >
                    <Users size={16} /> Assign Item
                  </button>
                )}
                {canCreate('pos_items') && onCreateItem && (
                  <button
                    onClick={() => onCreateItem(cat.id)}
                    style={{ ...styles.button, ...styles.buttonSecondary, padding: '8px 12px', fontSize: '13px' }}
                  >
                    <PackagePlus size={16} /> Create Item
                  </button>
                )}
                <div style={{ flex: 1 }} />
                {canUpdate('pos_categories') && (
                  <button onClick={() => handleEdit(cat)} style={{ ...styles.iconButton, color: '#4f46e5' }}>
                    <Edit size={18} />
                  </button>
                )}
                {canDelete('pos_categories') && (
                  <button onClick={() => handleDelete(cat)} style={{ ...styles.iconButton, color: '#ef4444' }}>
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {assigningCategory && (
        <PosCategoryAssignItemsModal
          token={token}
          category={assigningCategory}
          onClose={() => setAssigningCategory(null)}
          onSaved={() => fetchCategories(searchQuery)}
        />
      )}
    </div>
  );
};

export default PosCategories;
